/**
 * modifyRegistry — Phase 5.3.f sub-machine collapse.
 *
 * Mirror of `dimensionRegistry.ts` / `blockAuthoringRegistry.ts` for
 * MOVE / COPY / MIRROR / ROTATE / SCALE / OFFSET / STRETCH / TRIM /
 * EXTEND / FILLET / CHAMFER / BREAK / JOIN / EXPLODE / LENGTHEN /
 * ARRAYRECT / ARRAYPOLAR / ARRAYPATH / ARRAYEDIT / GCCONSTRAINT.
 *
 * Wraps the 60-step switches in `advanceModifySessionWithPoint` +
 * `advanceModifySessionWithText` (`draftModifyStateMachine.ts`) as
 * `Partial<Record<DraftStep, AdvanceWithPointFn|AdvanceWithTextFn>>`
 * entries so the unified `ADVANCE_WITH_POINT` / `ADVANCE_WITH_TEXT`
 * registries handle them alongside LINE / CIRCLE / DIM* / param-* /
 * action-*.
 *
 * The wrappers delegate verbatim — no behaviour change. PR 5.3.f
 * removes the `isModifyStep` predicate + delegation blocks in
 * `advanceDraftSessionWithPoint` / `advanceDraftSessionWithText`.
 *
 * **High-risk PR** per design §7: modify sub-machine carries
 * cross-handler state via `session.options.offsetSnapshot`,
 * `session.options.arrayEditGroupId`, etc. Verbatim delegation
 * preserves all that state — the wrapper is a thin pass-through.
 */

import {
  advanceModifySessionWithPoint,
  advanceModifySessionWithText,
} from "../draftModifyStateMachine";
import type {
  AdvanceWithPointFn,
  AdvanceWithTextFn,
} from "../draftTypes";

const MODIFY_STEPS = [
  "move-select",
  "move-base-point",
  "move-destination",
  "copy-select",
  "copy-base-point",
  "copy-destination",
  "mirror-select",
  "mirror-first-point",
  "mirror-second-point",
  "rotate-select",
  "rotate-base-point",
  "rotate-angle",
  "rotate-ref-start",
  "rotate-ref-end",
  "rotate-ref-new-angle",
  "scale-select",
  "scale-base-point",
  "scale-factor",
  "offset-distance",
  "offset-select-entity",
  "offset-side",
  "stretch-select-first",
  "stretch-select-second",
  "stretch-base",
  "stretch-destination",
  "trim-select-edges",
  "trim-pick-entity",
  "extend-select-edges",
  "extend-pick-entity",
  "fillet-radius",
  "fillet-first-entity",
  "fillet-second-entity",
  "chamfer-distances",
  "chamfer-first",
  "chamfer-second",
  "break-select",
  "break-first-point",
  "break-second-point",
  "join-select",
  "explode-select",
  "lengthen-select",
  "lengthen-mode",
  "lengthen-value",
  "lengthen-pick-end",
  "arrayrect-select",
  "arrayrect-base-point",
  "arrayrect-count",
  "arrayrect-spacing",
  "arraypolar-select",
  "arraypolar-center",
  "arraypolar-count",
  "arraypolar-fill",
  "arraypath-select",
  "arraypath-path",
  "arraypath-method",
  "arraypath-params",
  "arrayedit-select",
  "hatchedit-select",
  "hatchedit-pattern",
  "hatchedit-scale",
  "hatchedit-angle",
  "geom-constraint-pick-first",
  "geom-constraint-pick-second",
  "geom-constraint-pick-more",
  "geom-constraint-pick-reference-line",
] as const;

const modifyPointWrapper: AdvanceWithPointFn = (session, point, ctx) =>
  advanceModifySessionWithPoint(
    session,
    point,
    [...ctx.existingEntities],
    ctx.activeSpaceId,
    // HATCHEDIT select needs the typed entities (real boundary geometry) to
    // hit-test a clicked point against a HATCH — the preview-string coords the
    // generic `findClosestEntity` reads don't exist on hatch previews. Thread
    // `ctx.geometryEntities` through (the wrapper previously dropped it).
    ctx.geometryEntities,
  );

const modifyTextWrapper: AdvanceWithTextFn = (session, ctx) =>
  advanceModifySessionWithText(
    session,
    ctx.text,
    [...ctx.existingEntities],
    ctx.activeSpaceId,
    {},
    // HATCHEDIT select needs the typed entities (real boundary geometry) to
    // hit-test the composed point of a point-filter (FROM / M2P / .X.Y.Z)
    // resolved from TYPED command-line input against a HATCH — same reason the
    // point wrapper threads it. Without this the text path defaults to `[]` and
    // a filtered pick could never select a hatch. (Codex PR #504 P2.)
    ctx.geometryEntities,
  );

export const MODIFY_POINT_ENTRIES: ReadonlyArray<
  readonly [(typeof MODIFY_STEPS)[number], AdvanceWithPointFn]
> = MODIFY_STEPS.map((step) => [step, modifyPointWrapper]);

export const MODIFY_TEXT_ENTRIES: ReadonlyArray<
  readonly [(typeof MODIFY_STEPS)[number], AdvanceWithTextFn]
> = MODIFY_STEPS.map((step) => [step, modifyTextWrapper]);
